const REVISED_SSD_EFFECTIVE_DATE = new Date(2025, 6, 4);

export interface SellerStampDutyResult {
  amount: number;
  rate: number;
  rateLabel: string;
}

function parseLocalDate(value: string): Date | null {
  if (!value) return null;
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return null;

  const date = new Date(year, month - 1, day);
  return date.getFullYear() === year
    && date.getMonth() === month - 1
    && date.getDate() === day
    ? date
    : null;
}

function anniversaryOf(date: Date, years: number): Date {
  const anniversary = new Date(date);
  anniversary.setFullYear(anniversary.getFullYear() + years);
  return anniversary;
}

/**
 * Residential SSD schedule for current resales.
 * Purchases from 4 Jul 2025 use the revised four-year schedule; earlier
 * purchases use the three-year schedule introduced on 11 Mar 2017.
 */
export function calculateSellerStampDuty(
  purchaseDate: string,
  saleDate: string,
  salePrice: number,
): SellerStampDutyResult | null {
  const purchase = parseLocalDate(purchaseDate);
  const sale = parseLocalDate(saleDate);
  if (!purchase || !sale || sale <= purchase) return null;

  const revisedSchedule = purchase >= REVISED_SSD_EFFECTIVE_DATE;
  const rates = revisedSchedule ? [16, 12, 8, 4] : [12, 8, 4];

  const band = rates.findIndex((_, index) => sale < anniversaryOf(purchase, index + 1));
  if (band === -1) {
    const holdingYears = rates.length;
    return {
      amount: 0,
      rate: 0,
      rateLabel: `0% (held ${holdingYears} years or more, no SSD)`,
    };
  }

  const rate = rates[band];
  const holdingLabel = band === 0 ? '< 1 year' : `${band} to ${band + 1} years`;
  return {
    // IRAS requires SSD payable to be rounded down to the nearest dollar.
    amount: Math.floor(Math.max(0, salePrice) * rate / 100),
    rate,
    rateLabel: `${rate}% (held ${holdingLabel})`,
  };
}
